import React, { createContext, useContext, useState } from 'react';
import NotificationSnackbar from '../components/NotificationSnackbar';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [snackbars, setSnackbars] = useState([]);

  const getDefaultDuration = (severity) => {
    if (severity === 'error' || severity === 'warning') return 12000;
    return 6000;
  };

  const normalizeNotificationArgs = (severity, duration, options) => {
    if (duration && typeof duration === 'object' && !Array.isArray(duration)) {
      return {
        duration: getDefaultDuration(severity),
        options: duration
      };
    }

    return {
      duration: duration ?? getDefaultDuration(severity),
      options: options || {}
    };
  };

  const showNotification = (message, severity = 'success', duration = null, options = {}) => {
    const normalized = normalizeNotificationArgs(severity, duration, options);
    const nextSnackbar = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      open: true,
      message,
      severity,
      duration: normalized.duration,
      actionLabel: normalized.options.actionLabel || '',
      onClick: typeof normalized.options.onClick === 'function' ? normalized.options.onClick : null
    };
    setSnackbars((prev) => [nextSnackbar, ...prev]);
  };

  const hideNotification = (id) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
  };

  // Helper methods for specific notification types
  const showSuccess = (message, duration = null, options = {}) => showNotification(message, 'success', duration, options);
  const showError = (message, duration = null, options = {}) => showNotification(message, 'error', duration, options);
  const showWarning = (message, duration = null, options = {}) => showNotification(message, 'warning', duration, options);
  const showInfo = (message, duration = null, options = {}) => showNotification(message, 'info', duration, options);

  const value = {
    showNotification,
    hideNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationSnackbar snackbars={snackbars} onClose={hideNotification} />
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
